import { NoteForm } from '@/components/beneficiary/DocumentCreator'
import { storeActions } from '@/store/actions'
import { DocumentFile, Note } from '@/store/document'
import { useStoreDispatch } from '@/store/hooks'
import { documentFileSchema } from '@/validation-schemas'
import { Button } from '@repo/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/ui/form'
import { useFormValidator, useT } from '@repo/ui/hooks'
import { Textarea } from '@repo/ui/textarea'
import { useEffect, useMemo } from 'react'

export function NoteEditor({
  note,
  modalHandler,
}: {
  note: Note
  modalHandler: {
    open: boolean
    setOpen: (open: boolean) => void
  }
}) {
  const { t } = useT({ keyPrefix: 'noteEditor' })

  return (
    <Dialog open={modalHandler.open} onOpenChange={modalHandler.setOpen}>
      <DialogContent size="wide">
        <DialogHeader>
          <DialogTitle>{t('title')}</DialogTitle>
          <DialogDescription>{t('description')}</DialogDescription>
        </DialogHeader>

        <NoteForm
          type={note.type}
          editableNote={note}
          onSubmitted={() => modalHandler.setOpen(false)}
        />
      </DialogContent>
    </Dialog>
  )
}

export function DocumentFileEditor({
  document,
  modalHandler,
}: {
  document: DocumentFile
  modalHandler: {
    open: boolean
    setOpen: (open: boolean) => void
  }
}) {
  const { t } = useT({ keyPrefix: 'documentEditor' }) // FIXME: i18n

  return (
    <Dialog open={modalHandler.open} onOpenChange={modalHandler.setOpen}>
      <DialogContent size="wide">
        <DialogHeader>
          <DialogTitle>{t('title')}</DialogTitle>
          <DialogDescription>{t('description')}</DialogDescription>
        </DialogHeader>

        <DocumentFileForm
          editableDocument={document}
          onSubmitted={() => modalHandler.setOpen(false)}
        />
      </DialogContent>
    </Dialog>
  )
}

export function DocumentFileForm({
  editableDocument,
  newValues,
  onSubmitted,
}: {
  editableDocument?: DocumentFile
  newValues?: Pick<DocumentFile, 'title'>
  onSubmitted: () => void
}) {
  const dispatch = useStoreDispatch()
  const { tc } = useT({ keyPrefix: 'noteEditor' })

  const defaultValues = useMemo(
    () => ({ title: editableDocument?.title || '' }),
    [editableDocument],
  )

  const { form, isFormValid, submit, submitOnPressMetaEnter } =
    useFormValidator({
      defaultValues,
      onSubmit: ({ title }) => {
        if (editableDocument) {
          void dispatch(
            storeActions.beneficiary.document.update({
              id: editableDocument.id,
              title,
            }),
          )
        }
        form.reset(defaultValues)
        onSubmitted()
      },
      schema: documentFileSchema.shape,
    })

  useEffect(() => {
    if (newValues) form.reset(newValues)
  }, [newValues, form])

  return (
    <Form {...form}>
      <form
        onSubmit={submit}
        className="space-y-2"
        onKeyDown={submitOnPressMetaEnter}
      >
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{tc('title', { cfl: true })}</FormLabel>
              <FormControl>
                <Textarea
                  placeholder={tc('title', { cfl: true })}
                  {...field}
                  className="h-60 text-base"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <DialogFooter>
          <Button type="submit" disabled={!isFormValid}>
            {tc('update', { cfl: true })}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  )
}
